import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { IoLocationOutline } from "react-icons/io5";
import { BsBookmarkCheckFill } from "react-icons/bs";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { CiBookmarkPlus } from "react-icons/ci";
import { FaRegComment, FaRegHeart, FaHeart } from "react-icons/fa";
import { postNeighborhoodSavedNews } from "../../redux/ApiSlice/Neighborhood/NeighBorhoodNewsSavedSlice";
import { deleteNeighborhoodNews, likeNeighborhoodNews } from "../../redux/ApiSlice/Neighborhood/NeighborhoodNewsSlice";
import SkeletonLargeList from "../../components/skeleton/SkeletonLargeList";
import NeighborhoodNewsComments from "./NeighborhoodNewsComments";

function formatTimeOnly(dateString) {
    const date = new Date(dateString);
    const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    let timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    timeString = timeString.replace(/am|pm/i, match => match.toUpperCase());
    return `${formattedDate} at ${timeString}`;
}

const News = ({ data, loading, onDeleteSuccess, isOpen, setIsOpen, setReportNews, setSelectedNewsId, setBlockUserInfo, emptyMessage, userCreated }) => {
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const dispatch = useDispatch();

    const [activeMenuId, setActiveMenuId] = useState(null);
    const navigate = useNavigate();
    const [neighborhoodNewsList, setNeighborhoodNewsList] = useState([]);
    const [expandedNews, setExpandedNews] = useState({});
    const [commentDrawerNewsId, setCommentDrawerNewsId] = useState(null);

    useEffect(() => {
        if (Array.isArray(data)) {
            setNeighborhoodNewsList(data);
        } else {
            setNeighborhoodNewsList([]);
        }
    }, [data]);

    const handleToggleExpand = (id) => {
        setExpandedNews((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleClick = async (item, newsId, userId, userInfo = null) => {
        if (item === "Report") {
            setReportNews(true);
            setSelectedNewsId(newsId);
        } else if (item === "Block") {
            setIsOpen(userId);
            if (userInfo && setBlockUserInfo) {
                setBlockUserInfo(userInfo);
            }
        } else if (item === "Delete") {
            await dispatch(deleteNeighborhoodNews({ id: newsId })).unwrap().then(() => {
                toast.success("Neighborhood news deleted successfully.");
            });
        }
    };

    const handleLikeNeighborhoodNews = async (id) => {
        try {
            const response = await dispatch(likeNeighborhoodNews({ id })).unwrap();
            if (response.message === "News liked successfully.") {
                setNeighborhoodNewsList((prevNews) =>
                    prevNews.map((news) =>
                        news.id === id ? { ...news, likes_count: news.likes_count + 1, is_liked: true } : news
                    )
                );
            } else if (response.message === "News unliked successfully.") {
                setNeighborhoodNewsList((prevNews) =>
                    prevNews.map((news) =>
                        news.id === id ? { ...news, likes_count: news.likes_count - 1, is_liked: false } : news
                    )
                );
            } else {
                console.error("Failed to like neighborhood news:", response.message);
            }
        } catch (error) {
            console.error("Failed to like neighborhood news:", error);
        }
    };

    const handleNewsSave = async (id) => {
        try {
            const response = await dispatch(postNeighborhoodSavedNews(id)).unwrap();
            setNeighborhoodNewsList((prevNews) =>
                prevNews.map((news) =>
                    news.id === id ? { ...news, is_saved: response.detail === "News successfully saved." } : news
                )
            );
        } catch (error) {
            console.error("Failed to save neighborhood news:", error);
        }
    };

    const handleMenuToggle = (id, event) => {
        event.stopPropagation();
        setActiveMenuId((prev) => (prev === id ? null : id));
    };

    const handleClickOutside = useCallback((event) => {
        if (!event.target.closest('[data-menu-container]')) {
            setActiveMenuId(null);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    return (
        <>
            <div className="bg-white dark:bg-[#1E1D21] dark:text-white">
                <Toaster position="top-right" />
                <div className="mb-5">
                    {loading && <SkeletonLargeList />}

                    {!loading && Array.isArray(neighborhoodNewsList) && neighborhoodNewsList.map((news) => (
                        <div key={news.id} className="px-2 pb-1 mb-2 dark:text-gray-200 border-b border-gray-400 dark:border-gray-800">
                            <div className="flex items-start w-full">
                                <div className="w-14">
                                    <img
                                        alt={news.created_by.full_name}
                                        src={news.created_by.avatar}
                                        onClick={() => {
                                            if (User_profile?.id === news.created_by.id) {
                                                navigate('/account');
                                            } else {
                                                navigate(`/account/${news.created_by.id}`);
                                            }
                                        }}
                                        className="w-10 h-10 object-cover rounded-full cursor-pointer border border-gray-300 dark:border-gray-800"
                                    />
                                </div>
                                <div className="flex items-start justify-between w-full ml-1">
                                    <div className="flex flex-col">
                                        <p
                                            className="font-inter text-[12px] font-bold w-fit"
                                            onClick={() => {
                                                if (User_profile?.id === news.created_by.id) {
                                                    navigate('/account');
                                                } else {
                                                    navigate(`/account/${news.created_by.id}`);
                                                }
                                            }}
                                        >
                                            {news.created_by.full_name}
                                        </p>
                                        <p className="font-inter font-inter-regular text-[10px] text-gray-500">
                                            {formatTimeOnly(news.created_at)}
                                        </p>
                                        <p className="flex items-center font-inter font-inter-regular text-[10px] text-gray-500">
                                            <IoLocationOutline className="text-[14px] ml-[-3px]" />
                                            {news.news_location.city} {news.news_location.state}
                                        </p>
                                    </div>
                                    <div className="relative" data-menu-container>
                                        <HiOutlineDotsVertical
                                            className="text-[18px] cursor-pointer text-gray-600 mr-[-5px]"
                                            onClick={(event) => handleMenuToggle(news.id, event)}
                                        />
                                        {activeMenuId === news.id && (
                                            <div className="absolute right-0 mt-2 w-16 bg-white dark:bg-[#252222] border-2 border-[#BDE6FF] overflow-hidden rounded shadow-lg">
                                                {User_profile?.id !== news.created_by.id && (
                                                    <div
                                                        onClick={() => handleClick("Report", news.id)}
                                                        className="font-inter px-2 py-1 text-[12px] text-gray-700 hover:bg-gray-100 dark:text-gray-200 hover:text-black dark:hover:bg-[#2b2b2b] border-b border-gray-200 dark:border-gray-800 cursor-pointer"
                                                    >
                                                        Report
                                                    </div>
                                                )}
                                                {User_profile?.id !== news.created_by.id && (
                                                    <div
                                                        onClick={() => handleClick("Block", news.id, news.created_by.id, {
                                                            full_name: news.created_by.full_name
                                                        })}
                                                        className="font-inter px-2 py-1 text-[12px] text-gray-700 hover:bg-gray-100 dark:text-gray-200 hover:text-black dark:hover:bg-[#2b2b2b] border-none cursor-pointer"
                                                    >
                                                        Block
                                                    </div>
                                                )}
                                                {User_profile?.id === news.created_by.id && (
                                                    <div
                                                        onClick={() => navigate(`/neighborhood/news/update/${news.id}`)}
                                                        className="font-inter px-2 py-1 text-[12px] text-gray-700 hover:bg-gray-100 dark:text-gray-200 hover:text-black dark:hover:bg-[#2b2b2b] border-b border-gray-200 dark:border-gray-800 cursor-pointer"
                                                    >
                                                        Edit
                                                    </div>
                                                )}
                                                {User_profile?.id === news.created_by.id && (
                                                    <div
                                                        onClick={() => handleClick("Delete", news.id)}
                                                        className="font-inter px-2 py-1 text-[12px] text-gray-700 hover:bg-gray-100 dark:text-gray-200 hover:text-black dark:hover:bg-[#2b2b2b] border-none cursor-pointer"
                                                    >
                                                        Delete
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-2">
                                <p className="font-inter text-[12px] font-bold" onClick={() => navigate(`/neighborhood/news/${news.id}`)}>
                                    {news.title}
                                </p>
                                <img
                                    alt={news.title}
                                    src={news.post_image}
                                    onClick={() => navigate(`/neighborhood/news/${news.id}`)}
                                    className="w-full aspect-[4/3] object-cover rounded-lg mt-1 border border-gray-300 dark:border-gray-800 cursor-pointer"
                                />
                                <p
                                    className="font-inter text-[12px] font-semibold leading-[15px] mt-2 cursor-pointer"
                                    onClick={() => navigate(`/neighborhood/news/${news.id}`)}
                                >
                                    {news.news_description && news.news_description.split(" ").length > 20 && !expandedNews[news.id]
                                        ? (
                                            <>
                                                {news.news_description.split(" ").slice(0, 20).join(" ")}...
                                                <span
                                                    className="text-blue-500 ml-1"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleToggleExpand(news.id);
                                                    }}
                                                >
                                                    See more
                                                </span>
                                            </>)
                                        : (
                                            <>
                                                {news.news_description}
                                                {news.news_description && news.news_description.split(" ").length > 20 && (
                                                    <span
                                                        className="text-blue-500 ml-1"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleToggleExpand(news.id);
                                                        }}
                                                    >
                                                        Show less
                                                    </span>
                                                )}
                                            </>
                                        )
                                    }
                                </p>
                                <div className="flex items-center justify-between mt-1">
                                    <div className="flex items-center gap-x-4">
                                        <div className="flex flex-col items-center">
                                            {news.is_liked ? (
                                                <FaHeart className="text-[16px] text-red-600 cursor-pointer" onClick={() => handleLikeNeighborhoodNews(news.id)} />
                                            ) : (
                                                <FaRegHeart className="text-[16px] text-gray-600 cursor-pointer" onClick={() => handleLikeNeighborhoodNews(news.id)} />
                                            )}
                                            <p className="text-[10px] text-gray-500">{news.likes_count}</p>
                                        </div>
                                        <div className="flex flex-col items-center">
                                            <FaRegComment
                                                onClick={() => setCommentDrawerNewsId(news.id)}
                                                className="text-[16px] text-gray-600 cursor-pointer"
                                            />
                                            <p className="text-[10px] text-gray-500">{news.comments_count}</p>
                                        </div>
                                    </div>

                                    {news.is_saved ? (
                                        <BsBookmarkCheckFill className="text-[18px] text-gray-600 dark:text-blue-700 mr-[-2px] mt-[-5px] cursor-pointer" onClick={() => handleNewsSave(news.id)} />
                                    ) : (
                                        <CiBookmarkPlus className="text-[22px] text-gray-600 mr-[-5px] mt-[-5px] cursor-pointer" onClick={() => handleNewsSave(news.id)} />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {commentDrawerNewsId && (
                        <NeighborhoodNewsComments
                            newsId={commentDrawerNewsId}
                            isOpen={!!commentDrawerNewsId}
                            onClose={() => setCommentDrawerNewsId(null)}
                            onCommentCountChange={(newsId, delta) => {
                                setNeighborhoodNewsList((prev) =>
                                    prev.map((n) =>
                                        n.id === newsId ? { ...n, comments_count: Math.max(0, n.comments_count + delta) } : n
                                    )
                                );
                            }}
                        />
                    )}

                    {neighborhoodNewsList.length === 0 && !loading && (
                        <div className="flex flex-col h-[50vh] items-center justify-center mt-16 px-6">
                            <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-5 text-center max-w-xs">
                                {emptyMessage || "Share what's happening in your neighborhood. Be the first to post!"}
                            </p>
                            {userCreated && (
                                <button
                                    onClick={() => navigate("/neighborhood/news/create")}
                                    className="group relative px-6 py-2.5 bg-gradient-to-r from-[#BDE6FF] to-[#9dd4f5] hover:from-[#9dd4f5] hover:to-[#7dc5ed] text-black font-semibold rounded-full transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2 text-[13px]"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Create News
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default News;